import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, ThumbsUp, MessageSquare, ChevronDown, ChevronUp, Trash2, Send, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const priorityColors = {
  low: 'bg-slate-100 text-slate-600 border-slate-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-red-50 text-red-700 border-red-200',
};

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  'in-progress': 'bg-blue-50 text-blue-700 border-blue-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

const statusCards = [
  { key: 'all', label: 'All', icon: null, color: 'bg-white border-slate-200 text-slate-800', activeColor: 'bg-violet-600 border-violet-600 text-white' },
  { key: 'pending', label: 'Pending', icon: Clock, color: 'bg-white border-amber-200 text-amber-700', activeColor: 'bg-amber-500 border-amber-500 text-white' },
  { key: 'in-progress', label: 'In Progress', icon: AlertCircle, color: 'bg-white border-blue-200 text-blue-700', activeColor: 'bg-blue-500 border-blue-500 text-white' },
  { key: 'done', label: 'Resolved', icon: CheckCircle, color: 'bg-white border-emerald-200 text-emerald-700', activeColor: 'bg-emerald-500 border-emerald-500 text-white' },
  { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'bg-white border-red-200 text-red-600', activeColor: 'bg-red-500 border-red-500 text-white' },
];

export default function FeatureRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const fetchRequests = useCallback(() => {
    setLoading(true);
    api.get('/training/reports/feature-requests')
      .then(r => setRequests(r.data.requests || []))
      .catch(() => toast.error('Failed to load feature requests'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = requests.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterPriority !== 'all' && r.priority !== filterPriority) return false;
    return true;
  });

  // Count by status
  const counts = { all: requests.length, pending: 0, 'in-progress': 0, done: 0, rejected: 0 };
  requests.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });

  const handleUpvote = async (id) => {
    try {
      await api.post(`/training/reports/feature-requests/${id}/upvote`);
      fetchRequests();
    } catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/training/reports/feature-requests/${id}`, { status });
      fetchRequests();
      toast.success(`Status updated to ${status}`);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feature request?')) return;
    try {
      await api.delete(`/training/reports/feature-requests/${id}`);
      fetchRequests();
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="feature-requests-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Feature Requests</h1>
          <p className="text-sm text-slate-400 mt-1">{requests.length} request{requests.length !== 1 ? 's' : ''} total</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-violet-600 hover:bg-violet-700 rounded-sm text-xs" data-testid="add-feature-request-btn">
          <Plus className="w-4 h-4 mr-1" /> New Request
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-5 gap-3" data-testid="status-cards">
        {statusCards.map(sc => {
          const Icon = sc.icon;
          const isActive = filterStatus === sc.key;
          return (
            <Card
              key={sc.key}
              className={`border p-3 cursor-pointer transition-all hover:shadow-sm ${isActive ? sc.activeColor : sc.color}`}
              onClick={() => setFilterStatus(sc.key)}
              data-testid={`status-card-${sc.key}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-[9px] uppercase tracking-wider font-semibold ${isActive ? 'opacity-80' : 'opacity-60'}`}>{sc.label}</p>
                  <p className="text-2xl font-bold mt-0.5">{counts[sc.key]}</p>
                </div>
                {Icon && <Icon className={`w-5 h-5 ${isActive ? 'opacity-70' : 'opacity-30'}`} />}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Priority filter */}
      <div className="flex items-center gap-3">
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-36 h-9 text-xs rounded-sm" data-testid="filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        {filterStatus !== 'all' && (
          <Badge variant="outline" className="text-[10px] cursor-pointer hover:bg-slate-100" onClick={() => setFilterStatus('all')}>
            Showing: {statusCards.find(s => s.key === filterStatus)?.label} &times;
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border p-12 text-center">
          <p className="text-sm text-slate-400">
            {requests.length === 0 ? 'No feature requests yet. Click "New Request" to add one.' : `No ${filterStatus === 'all' ? '' : filterStatus + ' '}requests found.`}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(fr => (
            <FeatureRequestCard
              key={fr.id}
              fr={fr}
              expanded={expandedId === fr.id}
              onToggle={() => setExpandedId(expandedId === fr.id ? null : fr.id)}
              onUpvote={() => handleUpvote(fr.id)}
              onStatusChange={(s) => handleStatusChange(fr.id, s)}
              onDelete={() => handleDelete(fr.id)}
              onCommentAdded={fetchRequests}
            />
          ))}
        </div>
      )}

      <AddRequestDialog open={showAdd} onClose={() => setShowAdd(false)} onSaved={fetchRequests} />
    </div>
  );
}

function FeatureRequestCard({ fr, expanded, onToggle, onUpvote, onStatusChange, onDelete, onCommentAdded }) {
  return (
    <Card className="border overflow-hidden" data-testid={`feature-request-${fr.id}`}>
      <div className="flex items-start gap-3 p-4 cursor-pointer hover:bg-slate-50/50" onClick={onToggle}>
        <button
          className="flex flex-col items-center gap-0.5 mt-0.5 min-w-[40px]"
          onClick={e => { e.stopPropagation(); onUpvote(); }}
          data-testid={`upvote-${fr.id}`}
        >
          <ThumbsUp className={`w-4 h-4 ${fr.upvotes > 0 ? 'text-violet-600 fill-violet-100' : 'text-slate-300'}`} />
          <span className="text-xs font-semibold text-slate-600">{fr.upvotes || 0}</span>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-800">{fr.title}</h3>
            <Badge variant="outline" className={`text-[9px] ${priorityColors[fr.priority] || priorityColors.medium}`}>{fr.priority}</Badge>
            <Badge variant="outline" className={`text-[9px] ${statusColors[fr.status] || statusColors.pending}`}>{fr.status}</Badge>
          </div>
          {fr.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{fr.description}</p>}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
            <span>by {fr.created_by}</span>
            <span>{new Date(fr.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{(fr.comments || []).length}</span>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <Select value={fr.status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-28 h-7 text-[10px] rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete} data-testid={`delete-fr-${fr.id}`}>
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </Button>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t px-4 py-3 bg-slate-50/30">
          {fr.description && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Description</p>
              <p className="text-xs text-slate-600 whitespace-pre-wrap">{fr.description}</p>
            </div>
          )}
          <CommentsSection requestId={fr.id} comments={fr.comments || []} onCommentAdded={onCommentAdded} />
        </div>
      )}
    </Card>
  );
}

function CommentsSection({ requestId, comments, onCommentAdded }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/training/reports/feature-requests/${requestId}/comments`, { text: text.trim() });
      setText('');
      onCommentAdded();
    } catch { toast.error('Failed to post comment'); }
    setPosting(false);
  };

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Comments ({comments.length})</p>
      {comments.length > 0 && (
        <div className="space-y-2 mb-3">
          {comments.map(c => (
            <div key={c.id} className="bg-white border rounded-sm p-2.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-slate-700">{c.author}</span>
                <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p className="text-xs text-slate-600">{c.text}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          placeholder="Add a comment..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
          className="h-8 text-xs rounded-sm flex-1"
          data-testid={`comment-input-${requestId}`}
        />
        <Button size="sm" onClick={handlePost} disabled={posting || !text.trim()} className="h-8 rounded-sm text-xs bg-violet-600 hover:bg-violet-700" data-testid={`post-comment-${requestId}`}>
          <Send className="w-3 h-3 mr-1" />{posting ? '...' : 'Post'}
        </Button>
      </div>
    </div>
  );
}

function AddRequestDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      await api.post('/training/reports/feature-requests', form);
      toast.success('Feature request added');
      setForm({ title: '', description: '', priority: 'medium' });
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">New Feature Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Title *</label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Feature title" className="h-9 text-xs rounded-sm" data-testid="fr-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the feature..." className="text-xs rounded-sm min-h-[80px]" data-testid="fr-description-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Priority</label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger className="w-full h-9 text-xs rounded-sm" data-testid="fr-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 rounded-sm text-xs" data-testid="fr-submit-btn">
              {saving ? 'Saving...' : 'Submit Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
